import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  IMPERSONATION_APP_EVENTS,
  IMPERSONATION_USER_EVENTS,
  MAX_SESSIONS,
  MAX_WORKER_SESSIONS,
  MAX_WORKPLACE_SESSIONS,
} from "../constants";
import type { ImpersonationSession, ImpersonationSessionType } from "../types";
import { buildImpersonationUrl } from "../utils/buildImpersonationUrl";
import {
  generateSessionId,
  getDefaultWindowSize,
  getInitialBalloonPosition,
  getInitialWindowPosition,
  loadPersistedSessions,
  nextZIndex,
  persistSessions,
} from "../utils/sessionHelpers";
import { useSessionExpiry } from "./useSessionExpiry";
import { useToggleMaximizeSession } from "./useToggleMaximizeSession";
import { useViewportClamp } from "./useViewportClamp";

export function useImpersonationSessionsHook() {
  const { adminUserId: employeeId } = useSession();
  const [sessions, setSessions] = useState<ImpersonationSession[]>(loadPersistedSessions);
  const sessionsRef = useRef(sessions);
  const isInitialMount = useRef(true);

  useLayoutEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    persistSessions(sessions);
  }, [sessions]);

  const openSession = useCallback(
    (
      sessionType: ImpersonationSessionType,
      baseAppUrl: string,
      targetId: string,
      targetName: string,
      cognitoUsername: string
    ) => {
      let impersonateUrl: string;
      try {
        impersonateUrl = buildImpersonationUrl(baseAppUrl, targetId, cognitoUsername);
      } catch {
        showErrorToast("Unable to open impersonation session. Please try again.");
        return;
      }
      let event: { name: string; sessionId: string } | "limit" | undefined;

      setSessions((previous) => {
        const existing = previous.find(
          (session) => session.sessionType === sessionType && session.targetId === targetId
        );
        if (existing) {
          const zIndex = nextZIndex();
          event = {
            name: "restored",
            sessionId: existing.id,
          };

          return previous.map((session) =>
            session.id === existing.id
              ? { ...session, isMinimized: false, isActive: true, zIndex }
              : session
          );
        }

        const typeLimit =
          sessionType === "workplace" ? MAX_WORKPLACE_SESSIONS : MAX_WORKER_SESSIONS;
        const typeCount = previous.filter((s) => s.sessionType === sessionType).length;

        if (previous.length >= MAX_SESSIONS || typeCount >= typeLimit) {
          event = "limit";
          return previous;
        }
        const size = getDefaultWindowSize(sessionType);
        const newSession: ImpersonationSession = {
          id: generateSessionId(),
          sessionType,
          targetId,
          targetName,
          cognitoUsername,
          url: impersonateUrl,
          startedAt: Date.now(),
          isMinimized: false,
          isActive: true,
          // Workplace sessions open at the full viewport, so they start in
          // the "maximized" state and the header button should show Restore.
          // Worker sessions don't have the maximize button at all.
          isMaximized: sessionType === "workplace",
          position: getInitialWindowPosition(sessionType, previous.length, size),
          size,
          balloonPosition: getInitialBalloonPosition(previous.length),
          zIndex: nextZIndex(),
        };

        event = { name: "opened", sessionId: newSession.id };
        return [...previous, newSession];
      });

      if (event === "limit") {
        logEvent(IMPERSONATION_APP_EVENTS.SESSION_LIMIT_REACHED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
        });
        const perTypeLimit =
          sessionType === "workplace" ? MAX_WORKPLACE_SESSIONS : MAX_WORKER_SESSIONS;
        const typeCount = sessionsRef.current.filter((s) => s.sessionType === sessionType).length;
        const hitTypeLimit = typeCount >= perTypeLimit;
        showErrorToast(
          hitTypeLimit
            ? `Maximum ${perTypeLimit} ${sessionType} impersonation sessions. Close one first.`
            : `Maximum ${MAX_SESSIONS} total impersonation sessions. Close one first.`
        );
      } else if (event?.name === "restored") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
          sessionId: event.sessionId,
        });
      } else if (event?.name === "opened") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_STARTED, {
          employeeId,
          targetId,
          targetName,
          sessionType,
          sessionId: event.sessionId,
        });
      }
    },
    [employeeId]
  );

  const closeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_CLOSED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) => previous.filter((s) => s.id !== id));
    },
    [employeeId]
  );

  const minimizeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_MINIMIZED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: true } : s))
      );
    },
    [employeeId]
  );

  const restoreSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          targetId: session.targetId,
          targetName: session.targetName,
          sessionType: session.sessionType,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: false, isActive: true } : s))
      );
    },
    [employeeId]
  );

  const updateBalloonPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setSessions((previous) =>
      previous.map((session) =>
        session.id === id ? { ...session, balloonPosition: position } : session
      )
    );
  }, []);

  // Manual drags and resizes always exit "maximized" state and discard any
  // saved restore rect — once the user touches the window directly, the next
  // click on the maximize button should snapshot wherever they ended up.
  const exitMaximized = { isMaximized: false, restoreSize: undefined, restorePosition: undefined };
  const updateWindowPosition = useCallback(
    (id: string, position: { x: number; y: number }) =>
      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, position, ...exitMaximized } : s))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exitMaximized is a stable literal
    []
  );
  const updateWindowSize = useCallback(
    (id: string, size: { width: number; height: number }) =>
      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, size, ...exitMaximized } : s))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exitMaximized is a stable literal
    []
  );

  const toggleMaximizeSession = useToggleMaximizeSession({ employeeId, setSessions });

  const bringToFront = useCallback((id: string) => {
    const zIndex = nextZIndex();
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, zIndex } : session))
    );
  }, []);

  const updateSessionExpiresAt = useCallback((id: string, expiresAt: number) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, expiresAt } : session))
    );
  }, []);

  useSessionExpiry(sessions, setSessions, employeeId);

  useViewportClamp(sessions.length, setSessions);

  return {
    sessions,
    openSession,
    closeSession,
    minimizeSession,
    restoreSession,
    bringToFront,
    toggleMaximizeSession,
    updateBalloonPosition,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  };
}
